/**
 * SDS Faces management module — ported from Designer
 * Responsible for design faces — loading, switching and UI rendering
 */

import { store } from '../../../core/store.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { createLogger } from '../../../utils/logger.js'


const log = createLogger('sds-faces')

/**
 * Load and render the Faces list
 */
export function loadFaces() {
  const sdk = store.getState('design.sdkInstance')
  const isReady = store.getState('design.isSDKReady')

  if (!sdk || !isReady) {
    log.warn('SDK not ready, cannot load faces')
    return
  }

  try {
    let faces = []

    if (typeof sdk.getDesignFaces === 'function') {
      faces = sdk.getDesignFaces() || []
    }
    if (faces.length === 0 && typeof sdk.getFaces === 'function') {
      faces = sdk.getFaces() || []
    }
    if (faces.length === 0 && sdk.sarto && typeof sdk.sarto.getFaces === 'function') {
      faces = sdk.sarto.getFaces() || []
    }

    log.info('Faces data:', faces)

    const selectedIndex = faces.findIndex((f) => f.selected)
    if (selectedIndex >= 0) {
      store.setState('design.currentFace', faces[selectedIndex])
    }

    if (faces.length === 0) {
      log.warn('No faces returned - product may not support multiple faces')
      renderEmptyFaces()
      return
    }

    renderFaces(faces)
    log.info(`Loaded ${faces.length} faces`)
  } catch (error) {
    log.warn('Failed to load faces:', error.message)
    renderErrorFaces()
  }
}

/**
 * Render Faces list
 * @param {Array} faces - Faces data
 * @param {string} containerId - Target container element ID
 */
export function renderFaces(faces, containerId = 'facesList') {
  const facesList = document.getElementById(containerId)
  if (!facesList) {
    log.warn('Faces container not found:', containerId)
    return
  }

  facesList.innerHTML = ''
  const currentFace = store.getState('design.currentFace')

  faces.forEach((face, index) => {
    const isActive = face.selected || (currentFace && face.id === currentFace.id)

    const faceItem = document.createElement('div')
    faceItem.className = 'face-item' + (isActive ? ' active' : '')
    faceItem.setAttribute('data-face-id', face.id)
    faceItem.setAttribute('data-face-index', index)
    faceItem.onclick = () => selectFace(index, faces)

    const nameLabel = document.createElement('div')
    nameLabel.style.cssText =
      'position:absolute;bottom:0;left:0;right:0;background:var(--bg-overlay);color:var(--text-primary);font-size:9px;text-align:center;padding:1px 2px;line-height:1.2;pointer-events:none;'
    nameLabel.textContent = translateFaceName(face.name) || `${index + 1}`

    const img = document.createElement('img')
    const thumbnailUrl = face.thumbnail || face.url

    if (thumbnailUrl) {
      img.src = thumbnailUrl
    } else {
      img.src = createPlaceholderSvg(index + 1)
    }

    img.alt = face.name || `Face ${index + 1}`
    img.loading = 'eager'
    img.onerror = function () {
      this.src = createPlaceholderSvg(index + 1)
    }

    faceItem.appendChild(img)
    faceItem.appendChild(nameLabel)
    facesList.appendChild(faceItem)
  })
}

/**
 * Select a design face
 * @param {number} index - Design face index
 * @param {Array} faces - Design face list (optional)
 */
export function selectFace(index, faces = null) {
  const sdk = store.getState('design.sdkInstance')
  if (!sdk) return

  try {
    if (!faces) {
      if (typeof sdk.getDesignFaces === 'function') {
        faces = sdk.getDesignFaces() || []
      }
      if ((!faces || faces.length === 0) && typeof sdk.getFaces === 'function') {
        faces = sdk.getFaces() || []
      }
      if (!faces) faces = []
    }

    if (faces && faces[index]) {
      const face = faces[index]
      const faceName = face.name || `Face ${index + 1}`

      updateFaceActiveState(index)
      store.setState('design.currentFace', face)

      if (typeof sdk.switchDesignFaceById === 'function') {
        sdk.switchDesignFaceById(face.id, (result) => {
          if (result && result.status === 'success') {
            log.info('Face switched:', face.id, faceName)
            eventBus.emit(EVENTS.FACE_SELECT, face)
            loadFaces()
          } else {
            log.warn('Face switch callback status:', result)
          }
        })
      } else {
        sdk.switchFace(face.id)
        eventBus.emit(EVENTS.FACE_SELECT, face)
      }
    }
  } catch (error) {
    log.warn('Failed to select face:', error)
  }
}

/**
 * Immediately update Face list active state
 */
function updateFaceActiveState(activeIndex) {
  const containers = ['#facesList']
  containers.forEach((sel) => {
    const faceItems = document.querySelectorAll(`${sel} .face-item`)
    faceItems.forEach((item, i) => {
      item.classList.toggle('active', i === activeIndex)
    })
  })
}

/**
 * Translate face names to English
 */
function translateFaceName(faceName) {
  if (!faceName) return ''

  const translations = {
    front: 'Front',
    back: 'Back',
    'left side': 'Left Side',
    'right side': 'Right Side',
    top: 'Top',
    bottom: 'Bottom',
    internal: 'Inside',
    external: 'Outside',
    Front: 'Front',
    later: 'Back',
    above: 'Top',
    under: 'Bottom',
    left: 'Left',
    right: 'Right',
    just: 'Front',
    opposite: 'Back',
    forward: 'Front',
    Left: 'Left',
    superior: 'Top',
    Down: 'Bottom',
    Inside: 'Inside',
    outside: 'Outside',
    build: 'Lid',
    end: 'Base',
    side: 'Side',
    sleeve: 'Sleeve',
    neckline: 'Collar',
    pocket: 'Pocket',
    hat: 'Hood',
    'trouser legs': 'Leg',
    belt: 'Belt',
    vamp: 'Upper',
    sole: 'Sole',
    Tongue: 'Tongue',
    'Cup body': 'Body',
    'cup lid': 'Lid',
    'bottom of cup': 'Base',
    handle: 'Handle',
  }

  if (translations[faceName]) return translations[faceName]

  const hasChinese = /[\u4e00-\u9fff]/.test(faceName)
  if (hasChinese) {
    for (const [cn, en] of Object.entries(translations)) {
      if (faceName.includes(cn)) return en
    }
    return 'View'
  }

  return faceName
}

function createPlaceholderSvg(number) {
  return (
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48"%3E%3Crect fill="%231e2228" width="48" height="48"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%237a818c" font-family="Arial" font-size="10"%3E' +
    number +
    '%3C/text%3E%3C/svg%3E'
  )
}

function renderEmptyFaces() {
  const facesList = document.getElementById('facesList')
  if (facesList) facesList.innerHTML = ''
}

function renderErrorFaces() {
  const facesList = document.getElementById('facesList')
  if (facesList) {
    facesList.innerHTML =
      '<div class="placeholder-text" style="font-size:12px;color:var(--text-muted);">Faces unavailable</div>'
  }
}

export default {
  loadFaces,
  selectFace,
  renderFaces,
}
